import { FocusEventHandler, useRef, useState } from "react"
import { SignUpInputFieldProps } from "./SignUpInputField"
import { PasswordAlert } from "../PasswordAlert/PasswordAlert"
import "./SignUpInputField.css"

interface SubmitOnBlurInputFieldProps extends SignUpInputFieldProps {
	onBlur: FocusEventHandler<HTMLInputElement>
}

export function SubmitOnBlurInputField({ onBlur, ...props }: SubmitOnBlurInputFieldProps) {
	const {
		fieldName,
		inputFieldHandler,
		type,
		placeholder,
		initialValue,
		label,
		required,
		disabled,
		description,
		autofocus,
		autocomplete,
		passwordAlert,
		min,
		max,
		step,
		size = "full-width",
		labelPosition = "top",
	} = props

	const [showPasswordAs, setShowPasswordAs] = useState("password")
	const passwordRef = useRef(null)
	const handleShowPassword = () => {
		showPasswordAs === "password" ? setShowPasswordAs("text") : setShowPasswordAs("password")
	}
	const handleNumericInput = (e) => {
		const value = parseInt(e.target.value)
		if (value < min) {
			e.target.value = min
		} else if (value > max) {
			e.target.value = max
		}
	}

	return (
		<div className={`sign-up-input-field sign-up-input-field-label-position-${labelPosition}`}>
			<label htmlFor={fieldName} className={`bs-label ${disabled ? "label-disabled" : ""}`}>
				{label}
				{required && <span style={{ color: "var(--ara-red" }}>{" *"}</span>}
			</label>
			<div style={{ position: "relative" }}>
				<input
					className={`sign-up-input-${size}`}
					ref={passwordAlert ? passwordRef : null}
					autoFocus={autofocus}
					autoComplete={autocomplete}
					type={type === "password" ? showPasswordAs : type}
					placeholder={placeholder}
					name={fieldName}
					disabled={disabled}
					id={fieldName}
					value={initialValue}
					onChange={inputFieldHandler}
					onInput={type === "number" ? handleNumericInput : undefined}
					onBlur={onBlur}
					required={required}
					min={min}
					max={max}
					step={step}
				/>
				{fieldName.match(/password/i) && !disabled && (
					<span role="button" className="eye-icon" onClick={handleShowPassword}>
						{eyeIcon(showPasswordAs)}
					</span>
				)}
			</div>
			{description && <p className="sign-up-input-field-description">{description}</p>}
			{passwordAlert && <PasswordAlert passwordValue={initialValue} fieldRef={passwordRef} />}
		</div>
	)
}

function eyeIcon(inputType: string) {
	return inputType === "password" ? (
		<svg xmlns="http://www.w3.org/2000/svg" width="17" height="12" viewBox="0 0 17 12" fill="none">
			<path
				d="M8.82898 9.50215C9.73807 9.50215 10.5108 9.18132 11.1472 8.53965C11.7835 7.89798 12.1017 7.11882 12.1017 6.20215C12.1017 5.28548 11.7835 4.50632 11.1472 3.86465C10.5108 3.22298 9.73807 2.90215 8.82898 2.90215C7.91989 2.90215 7.14716 3.22298 6.5108 3.86465C5.87443 4.50632 5.55625 5.28548 5.55625 6.20215C5.55625 7.11882 5.87443 7.89798 6.5108 8.53965C7.14716 9.18132 7.91989 9.50215 8.82898 9.50215ZM8.82898 8.18215C8.28352 8.18215 7.81989 7.98965 7.43807 7.60465C7.05625 7.21965 6.86534 6.75215 6.86534 6.20215C6.86534 5.65215 7.05625 5.18465 7.43807 4.79965C7.81989 4.41465 8.28352 4.22215 8.82898 4.22215C9.37443 4.22215 9.83807 4.41465 10.2199 4.79965C10.6017 5.18465 10.7926 5.65215 10.7926 6.20215C10.7926 6.75215 10.6017 7.21965 10.2199 7.60465C9.83807 7.98965 9.37443 8.18215 8.82898 8.18215ZM8.82898 11.7021C7.05928 11.7021 5.44716 11.2041 3.99262 10.208C2.53807 9.21187 1.48352 7.87659 0.828979 6.20215C1.48352 4.5277 2.53807 3.19243 3.99262 2.19632C5.44716 1.2002 7.05928 0.702148 8.82898 0.702148C10.5987 0.702148 12.2108 1.2002 13.6653 2.19632C15.1199 3.19243 16.1744 4.5277 16.829 6.20215C16.1744 7.87659 15.1199 9.21187 13.6653 10.208C12.2108 11.2041 10.5987 11.7021 8.82898 11.7021ZM8.82898 10.2355C10.1987 10.2355 11.4563 9.87187 12.6017 9.14465C13.7472 8.41743 14.6229 7.43659 15.229 6.20215C14.6229 4.9677 13.7472 3.98687 12.6017 3.25965C11.4563 2.53243 10.1987 2.16882 8.82898 2.16882C7.45928 2.16882 6.20171 2.53243 5.05625 3.25965C3.9108 3.98687 3.03504 4.9677 2.42898 6.20215C3.03504 7.43659 3.9108 8.41743 5.05625 9.14465C6.20171 9.87187 7.45928 10.2355 8.82898 10.2355Z"
				fill="black"
			/>
		</svg>
	) : (
		<svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 16 15" fill="none">
			<path
				d="M10.9818 7.75741L9.92727 6.70774C10.0364 6.14068 9.87273 5.60982 9.43637 5.11515C9 4.62048 8.43637 4.42744 7.74546 4.53602L6.69091 3.48636C6.89697 3.38984 7.10606 3.31745 7.31818 3.26919C7.5303 3.22093 7.75758 3.1968 8 3.1968C8.90909 3.1968 9.68182 3.51351 10.3182 4.14692C10.9545 4.78034 11.2727 5.54949 11.2727 6.45437C11.2727 6.69568 11.2485 6.9219 11.2 7.13304C11.1515 7.34417 11.0788 7.5523 10.9818 7.75741ZM13.3091 10.0377L12.2545 9.02424C12.7152 8.67435 13.1242 8.29129 13.4818 7.87504C13.8394 7.45879 14.1455 6.98524 14.4 6.45437C13.7939 5.2358 12.9242 4.26757 11.7909 3.5497C10.6576 2.83183 9.39394 2.47289 8 2.47289C7.64849 2.47289 7.30303 2.49702 6.96364 2.54528C6.62424 2.59354 6.29091 2.66593 5.96364 2.76245L4.83636 1.6404C5.33333 1.43529 5.84242 1.28146 6.36364 1.17891C6.88485 1.07636 7.4303 1.02508 8 1.02508C9.8303 1.02508 11.4606 1.5288 12.8909 2.53623C14.3212 3.54367 15.3576 4.84972 16 6.45437C15.7212 7.16621 15.3545 7.82678 14.9 8.43607C14.4455 9.04535 13.9152 9.57924 13.3091 10.0377ZM14.4529 13.7C14.0217 14.1365 13.3187 14.1415 12.8813 13.7113L10.6182 11.4855C10.1939 11.6182 9.76667 11.7178 9.33636 11.7841C8.90606 11.8505 8.46061 11.8837 8 11.8837C6.1697 11.8837 4.53939 11.3799 3.10909 10.3725C1.67879 9.36508 0.642424 8.05903 0 6.45437C0.254545 5.81492 0.575758 5.22072 0.963636 4.67176C1.35152 4.12279 1.79394 3.63114 2.29091 3.1968L0.290909 1.16986C0.722314 0.737672 1.42221 0.73645 1.85512 1.16713L14.4529 13.7ZM3.30909 4.21027C2.95758 4.52396 2.63636 4.86781 2.34545 5.24183C2.05455 5.61585 1.80606 6.02003 1.6 6.45437C2.20606 7.67295 3.07576 8.64117 4.20909 9.35905C5.34242 10.0769 6.60606 10.4359 8 10.4359C8.24242 10.4359 8.47879 10.4208 8.70909 10.3906C8.93939 10.3604 9.17576 10.3273 9.41818 10.2911L8.76364 9.60336C8.6303 9.63956 8.50303 9.66671 8.38182 9.6848C8.26061 9.7029 8.13333 9.71195 8 9.71195C7.09091 9.71195 6.31818 9.39524 5.68182 8.76182C5.04545 8.12841 4.72727 7.35926 4.72727 6.45437C4.72727 6.32166 4.73636 6.19497 4.75455 6.07432C4.77273 5.95367 4.8 5.82699 4.83636 5.69427L3.30909 4.21027Z"
				fill="black"
			/>
		</svg>
	)
}
